import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import internHero from '../../assets/img/internship.jpg';
import techIcon from '../../assets/img/services/24 hour.png';
import designIcon from '../../assets/img/services/cloud migration.png';
import marketingIcon from '../../assets/img/services/cloud services.png';
import dataIcon from '../../assets/img/services/creative designer.png';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  GlassCard,
  PremiumCTA,
  inputClass,
  btnPrimaryClass,
} from '../premium/PremiumPageUI';
import { submitFormEmail } from '../../lib/submitFormEmail';

const internshipPositions = [
  { id: 1, title: 'Software Development', icon: techIcon, positions: ['Frontend Developer', 'Backend Developer', 'Mobile App Developer'], duration: '3-6 months', gradient: 'from-[#2563EB] to-cyan-500' },
  { id: 2, title: 'UI/UX Design', icon: designIcon, positions: ['UI Designer', 'UX Researcher', 'Product Designer'], duration: '3-6 months', gradient: 'from-violet-500 to-purple-500' },
  { id: 3, title: 'Digital Marketing', icon: marketingIcon, positions: ['Social Media', 'Content Marketing', 'SEO'], duration: '3-6 months', gradient: 'from-pink-500 to-rose-500' },
  { id: 4, title: 'Data Science', icon: dataIcon, positions: ['Data Analyst', 'ML Engineer', 'Business Intelligence'], duration: '3-6 months', gradient: 'from-emerald-500 to-teal-500' },
];

const benefits = [
  'Real-world project experience',
  'Mentorship from industry experts',
  'Networking opportunities',
  'Flexible hybrid work environment',
  'Performance-based hiring potential',
  'Certificate upon completion',
  'Learning & development sessions',
  'Team building activities',
];

const faqs = [
  { q: 'Is the internship paid?', a: 'Yes—all internships include competitive stipends based on role and experience.' },
  { q: 'What is the application process?', a: 'Submit your application, complete a technical interview, and receive an offer within two weeks.' },
  { q: 'Remote or in-office?', a: 'We offer hybrid and remote opportunities depending on the role.' },
];

export default function ApplyInternship() {
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', education: '', position: '', startDate: '', coverLetter: '' });
  const [resume, setResume] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await submitFormEmail({
        formName: 'Internship Application',
        fields: {
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          education: formData.education,
          track: formData.position || selectedPosition,
          startDate: formData.startDate,
          coverLetter: formData.coverLetter,
        },
        files: resume ? { resume } : {},
      });
      alert('Application submitted successfully!');
      setFormData({ fullName: '', email: '', phone: '', education: '', position: '', startDate: '', coverLetter: '' });
      setResume(null);
      setSelectedPosition(null);
    } catch (error) {
      alert(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageShell>
      <section className="relative pt-28 overflow-hidden">
        <div className="absolute inset-0 h-[420px]">
          <img src={internHero} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-slate-50/95 to-white dark:from-gray-900/95 dark:to-gray-900" />
        </div>
        <div className="relative">
          <PremiumHero
            badge="Internship Program"
            title="Launch Your"
            highlight="Career"
            subtitle="Work on cutting-edge projects alongside industry experts—gain real experience, mentorship, and a path to full-time roles."
            stats={[
              { value: '4', label: 'Tracks' },
              { value: '3-6', label: 'Months' },
              { value: 'Paid', label: 'Stipends' },
              { value: 'Hybrid', label: 'Format' },
            ]}
            actions={[
              { label: 'View Positions', primary: true, onClick: () => document.getElementById('positions')?.scrollIntoView({ behavior: 'smooth' }) },
              { label: 'Career Page', href: '/career' },
            ]}
          />
        </div>
      </section>

      <PremiumSection>
        <SectionHeader badge="Program" title="Why Choose" highlight="Us" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
          {benefits.map((b) => (
            <div key={b} className="flex items-center gap-2 p-3 rounded-xl hero-glass-tag text-sm text-slate-600 dark:text-gray-300">
              <CheckCircleIcon className="w-5 h-5 text-[#2563EB] flex-shrink-0" />
              {b}
            </div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection id="positions" alt>
        <SectionHeader badge="Tracks" title="Internship" highlight="Positions" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {internshipPositions.map((pos) => (
              <motion.div key={pos.id} whileHover={{ y: -8 }} onClick={() => setSelectedPosition(pos.title)} className={`cursor-pointer ${selectedPosition === pos.title ? 'ring-2 ring-[#2563EB] rounded-2xl' : ''}`}>
                <GlassCard className="p-6 h-full">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${pos.gradient} flex items-center justify-center mb-4 p-2`}>
                    <img src={pos.icon} alt="" className="w-full h-full object-contain" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-2">{pos.title}</h3>
                  <div className="flex items-center gap-1 text-xs text-slate-500 mb-3"><ClockIcon className="w-4 h-4" />{pos.duration}</div>
                  <ul className="space-y-1">
                    {pos.positions.map((p) => (
                      <li key={p} className="text-xs text-slate-600 dark:text-gray-400">• {p}</li>
                    ))}
                  </ul>
                </GlassCard>
              </motion.div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection>
        <SectionHeader badge="Apply" title="Submit Your" highlight="Application" />
        <GlassCard className="max-w-2xl mx-auto p-8 md:p-10" hover={false}>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-2">Full Name</label>
                <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} className={inputClass} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Education</label>
                <input type="text" name="education" required value={formData.education} onChange={handleChange} className={inputClass} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium mb-2">Track</label>
                <select name="position" required value={formData.position || selectedPosition || ''} onChange={handleChange} className={inputClass}>
                  <option value="">Select track</option>
                  {internshipPositions.map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <input type="date" name="startDate" required value={formData.startDate} onChange={handleChange} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Cover Letter</label>
              <textarea name="coverLetter" rows={4} value={formData.coverLetter} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Resume (PDF)</label>
              <input type="file" accept=".pdf,.doc,.docx" className={inputClass} onChange={(e) => setResume(e.target.files?.[0] || null)} />
            </div>
            <button type="submit" disabled={isSubmitting} className={`${btnPrimaryClass} w-full disabled:opacity-60`}>
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </GlassCard>
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="FAQ" title="Common" highlight="Questions" />
        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {faqs.map((faq) => (
            <GlassCard key={faq.q} className="p-6">
              <h3 className="font-bold mb-2">{faq.q}</h3>
              <p className="text-sm text-slate-600 dark:text-gray-300">{faq.a}</p>
            </GlassCard>
          ))}
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Ready to Start Your Journey?"
        subtitle="Join the next cohort of innovators building the future with Nisunajni Group."
        primary={{ label: 'Explore Academy', href: '/academy' }}
        secondary={{ label: 'Contact HR', href: '/contact' }}
      />
    </PageShell>
  );
}
